import React, { useEffect, useState } from 'react'
import { Button, Checkbox, ResponsiveTableRow, Row } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { useParams, useRouteMatch } from '@fs/zion-router'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import { usePageTitle } from '../../app/PageTitleContext'
import useNotifications from '../../../hooks/useNotifications'
import { DmcText } from '../../common/DmcText'
import { DmcGrid } from '../../common/DmcGrid'
import { DmcRow } from '../../common/DmcRow'
import { getQueues } from '../../../services/dmqueuesService'
import { navigationQueryParams, treeCategoryMap } from '../messagesList/useMessagesListController'

const types = {
  message: { queues: ['CMISX_MISMATCH'] },
  holocaust: {
    queues: ['HOLOCAUST_HIGH', 'HOLOCAUST_LOW', 'HOLOCAUST_ORDINANCE_REPORT', 'HOLOCAUST_CONTRIBUTORS'],
  },
  famous: { queues: ['FAMOUS_PERSON', 'RELIGIOUS_TITLE', 'EARLY_CHURCH_LEADERS'] },
  historical: { queues: ['HISTORICAL'] },
  inappropriate: {
    queues: [
      'PROFANITY',
      'ONE_HUNDRED_TEN_YEAR',
      'INAPPROPRIATE',
      'SLAVERY_ASSOCIATION',
      'DEITY',
      'BIRTH_DATE_CHANGE_WITH_RESERVATIONS',
      'MEMORIES_ADDED',
    ],
  },
  sensitive: {
    queues: ['SENSITIVE', 'HYBRID_SENSITIVE', 'RESERVATION_HELD_BY_NON_CONTRIBUTORS'],
    excluded: ['SENSITIVE_REEVALUATION', 'HYBRID_SENSITIVE_REEVALUATION'],
  },
  gender: { queues: ['SAME_SEX', 'SEX_CHANGED', 'WRONG_GENDER'] },
  fictional: { queues: ['MYTHOLOGICAL', 'FICTITIOUS'] },
  reevaluate: { queues: ['HYBRID_SENSITIVE_REEVALUATION', 'SENSITIVE_REEVALUATION'] },
  secondary: { queues: ['SECONDARY'] },
}

const CATEGORY_OPTIONS = [treeCategoryMap.familyTree, treeCategoryMap.cet, treeCategoryMap.secondary]

export default function QueuesList() {
  usePageView({ page_detail: `DMConsole:QueuesList:${useParams().queuesFilter}` })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.queues.list'))
  const { error } = useNotifications()
  const [queuesList, setQueuesList] = useState([])
  const queuesFilter = useParams().queuesFilter

  useEffect(() => {
    if (queuesFilter) {
      getQueues()
        .then((queues) => {
          setQueuesList(queues.data)
        })
        .catch((err) => {
          error(
            t('queues.errors.failedToGetDMQueues', `Failed to get Data Management Queues: {message}`, {
              message: err?.message,
            }),
            err
          )
          setQueuesList([])
        })
    } else {
      error(t('typeIsNull', 'Type is null'))
    }
  }, [queuesFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!queuesList) {
    return null
  }

  return <QueuesTable queuesList={queuesList} />
}

function QueuesTable({ queuesList }) {
  const { t } = useTranslation()
  const queuesFilter = useParams().queuesFilter
  const filter = types[queuesFilter]

  let filteredQueues = queuesList
  if (filter && Array.isArray(filter.queues)) {
    filteredQueues = queuesList.queues?.filter((q) => {
      return (
        filter.queues.some((name) => q.name.includes(name)) &&
        !filter.excluded?.some((excludedName) => q.name.includes(excludedName)) &&
        q.messageCount > 0
      )
    })
  }

  const [selected, setSelected] = useState(() => [treeCategoryMap.familyTree, treeCategoryMap.cet])

  const handleCheckboxChange = (value) => {
    setSelected((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]))
  }

  return (
    <>
      <Row>
        {CATEGORY_OPTIONS.map((category) => (
          <Checkbox
            key={category}
            label={t(`treeCategories.${category}`, category)}
            name={category}
            checked={selected.includes(category)}
            onChange={() => handleCheckboxChange(category)}
          />
        ))}
      </Row>
      <DmcResponsiveTable
        totalColumnCount={1}
        atSizeColumns={{
          default: {
            labels: [''],
          },
        }}
      >
        {filteredQueues?.map((queue) => {
          return (
            <ResponsiveTableRow
              key={queue.id}
              column1={queue.messageCount > 0 && <QueueTableDetails queue={queue} selected={selected} />}
            />
          )
        })}
      </DmcResponsiveTable>
    </>
  )
}

function QueueTableDetails({ queue, selected }) {
  const { t } = useTranslation()
  const match = useRouteMatch()

  const noneLabel = t('treeCategories.noTreeCategory', 'No Tree Category')
  const treeCategories = queue.treeCategoryCounts?.none ? '' : selected

  return (
    <DmcGrid columns={[3, 4, 3, 0.5, 3]}>
      <DmcText text={queue.name} />
      <DmcText text={queue.description} />
      <span>
        {!queue.treeCategoryCounts && <DmcText text={queue.messageCount} />}
        {queue.treeCategoryCounts &&
          Object.entries(queue.treeCategoryCounts)
            .reduce((acc, [key, value]) => {
              const group = key === 'sharedFamilyGroup' ? 'familyTree' : key
              const existingEntry = acc.find(([k]) => k === group)
              if (existingEntry) {
                existingEntry[1] += value
              } else {
                acc.push([group, value])
              }
              return acc
            }, [])
            .filter(([key]) => selected.includes(treeCategoryMap[key]))
            .sort(([keyA], [keyB]) => {
              const indexA = CATEGORY_OPTIONS.indexOf(treeCategoryMap[keyA])
              const indexB = CATEGORY_OPTIONS.indexOf(treeCategoryMap[keyB])
              return indexA - indexB
            })
            .map(([key, value]) => {
              const label = t(`treeCategories.${treeCategoryMap[key]}`, key)
              return <DmcText key={key} text={`${label}: ${value}`} />
            })}
        {queue.treeCategoryCounts?.none && <DmcText text={`${noneLabel}: ${queue.treeCategoryCounts.none}`} />}
      </span>
      <div>
        {queue.messageCount > 0 && (
          <DmcRow>
            <Button
              to={`${match.url}/${queue.name}?${navigationQueryParams({ queuePageIndex: 0, treeCategories })}`}
            >{`${t('queues.view', 'View')}`}</Button>
          </DmcRow>
        )}
      </div>
    </DmcGrid>
  )
}
